/* ========== WATERFALL CHART ==========
 * Excel's modern waterfall uses three semantic colors:
 *   Increase (positive change) → accent1 blue
 *   Decrease (negative change) → accent2 orange
 *   Total / Subtotal           → gray
 *
 * Subtotal points (chartObj.subtotals — parsed from
 * <cx:layoutPr><cx:subtotals><cx:idx val="N"/>) are NOT deltas: they
 * restart the bar from zero at the current running value. Treating them
 * as deltas doubles the cumulative sum and shows wrong data.
 *
 * Per-point overrides via series.pointColors[i] still apply when present
 * (Excel writes them when the user manually colors a bar).
 * ===================================== */

import { drawAxes, drawGridlines, drawCategoryLabels } from './utils';

// Excel's standard Office theme colors for Waterfall semantics.
var COLOR_INCREASE = '#4472C4'; // accent1 (Office blue)
var COLOR_DECREASE = '#ED7D31'; // accent2 (orange)
var COLOR_TOTAL    = '#A5A5A5'; // accent3 (gray)

export function drawWaterfallChart(ctx, x, y, width, height, chartObj) {
  var series = chartObj && chartObj.series && chartObj.series[0] ? chartObj.series[0] : null;
  if (!series || series.data.length === 0) return;
  var data = series.data;
  var cats = chartObj.categories || [];
  var pointColors = series.pointColors || [];
  var numBars = data.length;

  // Subtotal/total indices from the chartEx layout properties.
  var subtotalSet = {};
  (chartObj.subtotals || []).forEach(function (i) { subtotalSet[i] = true; });

  var cumulative = 0;
  var segments = data.map(function (val, i) {
    if (subtotalSet[i]) {
      // Total bar: spans 0 → current running value. Excel keeps the
      // running sum unchanged (the cached total value is informational).
      return { start: 0, end: cumulative, value: cumulative, isTotal: true };
    }
    var start = cumulative;
    cumulative += val;
    return { start: start, end: cumulative, value: val, isTotal: false };
  });

  var allEnds = segments.map(function (s) { return s.end; });
  var allStarts = segments.map(function (s) { return s.start; });
  var minValue = Math.min.apply(null, [0].concat(allStarts).concat(allEnds));
  var maxValue = Math.max.apply(null, [0].concat(allStarts).concat(allEnds));
  var range = maxValue - minValue || 100;

  drawGridlines(ctx, x, y, width, height, minValue, range);

  var groupWidth = width / numBars;
  var barWidth = groupWidth * 0.6;

  segments.forEach(function (seg, i) {
    var startY = y + height - ((seg.start - minValue) / range) * height;
    var endY = y + height - ((seg.end - minValue) / range) * height;
    var barTop = Math.min(startY, endY);
    var barH = Math.max(Math.abs(startY - endY), 1);
    var barX = x + i * groupWidth + (groupWidth - barWidth) / 2;

    // Color priority: per-point override → semantic (total/increase/decrease)
    ctx.fillStyle = pointColors[i]
      || (seg.isTotal ? COLOR_TOTAL : (seg.value >= 0 ? COLOR_INCREASE : COLOR_DECREASE));
    ctx.fillRect(barX, barTop, barWidth, barH);

    // Solid connector line to next bar's start
    if (i < numBars - 1) {
      ctx.strokeStyle = '#A6A6A6';
      ctx.lineWidth = 1;
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(barX + barWidth, endY);
      ctx.lineTo(x + (i + 1) * groupWidth + (groupWidth - barWidth) / 2, endY);
      ctx.stroke();
    }
  });

  drawCategoryLabels(ctx, x, y, height, cats, numBars, groupWidth);
  drawAxes(ctx, x, y, width, height);
}
